import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import * as d3 from 'd3';
import distinctColors from 'distinct-colors';

import { useMeasure } from 'react-use';
import { setSelectedVariantAndUpdateRange } from '../../../actions/GenomeActions';
import { SEQ_CAT_COLORS } from '../../../settings';
import s from './VariantScatterContainer.css';
import { getColorScale } from '../../../core/doc_util';

const MIN_INIT_VARIANTS = 100;
const SVG_HEIGHT = 300;
const NUM_TICKS = 5;
const LIGHT_GRAY = '#BBBBBB';
const DARK_GRAY = '#787878';

const chrDataArr = [];
const chrDataDict = {};
for (let i = 1; i < 25; i += 1) {
  const chrName = i < 23 ? `chr${i}` : i < 24 ? 'chrX' : 'chrY';
  const chromData = { name: chrName, colorIdx: i - 1 };
  chrDataArr.push(chromData);
  chrDataDict[chrName] = chromData;
}

const colors = distinctColors({
  count: chrDataArr.length,
  hueMin: 50,
  hueMax: 340,
  chromaMin: 0,
  chromaMax: 90,
  lightMin: 45,
  lightMax: 80,
}).map(color => color.hex());
const grayColors = new Array(chrDataArr.length);

const chrColor = getColorScale(Object.keys(chrDataArr), colors); // domain param must include only numbers

const VariantScatterContainer = ({
  dataRegions,
  manhattan,
  seqClass,
  selectedVariantScoreType,
  sortedData,
  tickFormat,
  inputType,
}) => {
  const seqColor = d => {
    // Check if selected score type is a sequence class
    let cat = Object.keys(SEQ_CAT_COLORS).find(category =>
      selectedVariantScoreType.name.startsWith(category),
    );
    // Otherwise use max_seq_class
    if (!cat)
      cat = Object.keys(SEQ_CAT_COLORS).find(category =>
        d.max_seq_class.startsWith(category),
      );
    return SEQ_CAT_COLORS[cat];
  };

  const [divRef, { width: containerWidth }] = useMeasure();
  const selectedVariant = useSelector(state => state.genome.selectedVariant);
  const genome = useSelector(state => state.genome);
  const svgRef = useRef(null);
  const canvasRef = useRef(null);
  const tooltipRef = useRef(null);

  dataRegions[selectedVariantScoreType.short_name].forEach((region, i) => {
    const { colorIdx } = chrDataDict[region.chromosome];
    if (i % 2) grayColors[colorIdx] = LIGHT_GRAY;
    else grayColors[colorIdx] = DARK_GRAY;
  });

  const brushColors = manhattan && !seqClass ? colors : grayColors;

  const hijackTickFormat = (d, idx, arr) => {
    // Remove the final tick since it will not fit
    if (idx === arr.length - 1) return '';
    return tickFormat(d);
  };

  function getDefaults(dataParam) {
    let tmpMinY = 0;
    let tmpMaxY = 0;
    const tmpDefaultLimit = Math.min(
      dataParam.length,
      Math.max(MIN_INIT_VARIANTS, Math.floor(dataParam.length / 10)),
    );

    dataParam.forEach(datum => {
      tmpMinY = Math.min(tmpMinY, datum.y);
      tmpMaxY = Math.max(tmpMaxY, datum.y);
    });

    const tmpDefaultDomain = {
      x: [dataParam[0].x, dataParam[tmpDefaultLimit - 1].x],
      y: [tmpMinY, tmpMaxY],
    };
    return {
      defaultDomain: tmpDefaultDomain,
      defaultLimit: tmpDefaultLimit,
    };
  }

  const variantColor = d =>
    seqClass ? seqColor(d) : chrColor(chrDataDict[d.chromosome].colorIdx);

  const dispatch = useDispatch();
  const [formattedData, setFormattedData] = useState({});
  const [minY, setMinY] = useState(0);
  const [maxY, setMaxY] = useState(0);
  const [reticlePosition, setReticlePosition] = useState([0, 0]);
  const [xDomain, setXDomain] = useState(null);

  useEffect(() => {
    const { defaultDomain } = getDefaults(sortedData);
    const tmpMinY = defaultDomain.y[0];
    const tmpMaxY = defaultDomain.y[1];

    setMinY(tmpMinY);
    setMaxY(tmpMaxY);
  }, [sortedData]);

  // Following variables are extracted from D3 section to preserve brush domain
  const margin = { top: 0, right: 0, bottom: 50, left: 40 };
  const width = Math.max(containerWidth - margin.left - margin.right, 0);
  const xDomainRatio =
    width / (sortedData[sortedData.length - 1].x - sortedData[0].x);
  const radius = 6;

  useEffect(() => {
    const tmpFormattedData = {};
    sortedData.forEach(datum => {
      if (tmpFormattedData[datum.chromosome])
        tmpFormattedData[datum.chromosome].push(datum);
      else tmpFormattedData[datum.chromosome] = [datum];
    });
    setFormattedData(tmpFormattedData);
  }, [sortedData]);

  useEffect(() => {
    // Reset to the default xDomain only when the score selections change
    setXDomain(null);
  }, [selectedVariantScoreType, manhattan]);

  useEffect(() => {
    if (minY === maxY || !Object.keys(formattedData).length) return () => {};

    const brushRadius = radius / 3;
    const svg = d3.select(svgRef.current);
    const canvas = d3.select(canvasRef.current);
    const tooltip = d3.select(tooltipRef.current);
    const currentContext = svg.append('g');
    const height = SVG_HEIGHT - margin.top - margin.bottom;
    const margin2 = {
      top: height + 16,
      right: margin.right,
      bottom: 0,
      left: margin.left,
    };
    const height2 = SVG_HEIGHT - margin2.top - margin2.bottom;

    const x = d3
      .scaleLinear()
      .domain([
        sortedData[0].x - (radius * 2) / xDomainRatio,
        sortedData[sortedData.length - 1].x + (radius * 2) / xDomainRatio,
      ])
      .range([0, width]);

    let xDomainOrigin = x.domain()[0];
    let xDomainSize = x.domain()[1] - x.domain()[0];
    if (xDomainSize === Infinity) return () => {};

    const y = d3
      .scaleLinear()
      .domain([minY, maxY])
      .range([height - radius * 3, radius * 3]);

    const x2 = d3
      .scaleLinear()
      .domain([x.domain()[0], x.domain()[1]])
      .range([0, width]);
    const y2 = d3
      .scaleLinear()
      .domain([y.domain()[0], y.domain()[1]])
      .range([height2 - brushRadius * 2, brushRadius * 2]);

    // Remove old gradient if exists
    const oldGradient = svg.selectAll('#brushGradient').node();
    if (oldGradient) oldGradient.parentNode.remove();

    // Add a linear gradient to manhattan brush to delineate chromosomes
    const linearGradient = svg
      .append('defs')
      .append('linearGradient')
      .attr('id', 'brushGradient');

    // Generate the color stops for each section of the brush
    // Ref -> https://codepen.io/chriscoyier/pen/csgoD
    dataRegions[selectedVariantScoreType.short_name].forEach(
      (region, i, arr) => {
        const { colorIdx } = chrDataDict[region.chromosome];
        // First stop needs to be solo
        if (i === 0) {
          linearGradient
            .append('stop')
            .attr('stop-color', brushColors[colorIdx]);
          return;
        }

        const prevColorIdx = chrDataDict[arr[i - 1].chromosome].colorIdx;
        const pct = `${(x2(
          arr[i - 1].endRegionPosition +
            (arr[i].beginRegionPosition - arr[i - 1].endRegionPosition) / 2,
        ) /
          x2.range()[1]) *
          100}%`;

        // The rest are paired
        linearGradient
          .append('stop')
          .attr('offset', pct)
          .attr('stop-color', brushColors[prevColorIdx]);
        linearGradient
          .append('stop')
          .attr('offset', pct)
          .attr('stop-color', brushColors[colorIdx]);
      },
    );

    // Get coordinates of selected variant
    let selectedVariantCoordinates = { x: -1000, y: -1000 };
    if (
      Object.keys(selectedVariant).length &&
      formattedData[selectedVariant.chromosome]
    ) {
      const match = formattedData[selectedVariant.chromosome].find(
        v =>
          v.position === selectedVariant.position &&
          v.ref_allele === selectedVariant.ref_allele &&
          v.alt_allele === selectedVariant.alt_allele,
      );
      selectedVariantCoordinates = {
        x: match.x,
        y: match.y,
      };
    }

    // Reset the brush domain
    if (xDomain) x.domain(xDomain);

    function getCurrentXTicks() {
      xDomainOrigin = x.domain()[0];
      xDomainSize = x.domain()[1] - x.domain()[0];
      return [...Array(NUM_TICKS + 2)].map((_, i) =>
        parseInt((xDomainSize / (NUM_TICKS + 1)) * i + xDomainOrigin, 10),
      );
    }

    function getGridLines() {
      const ticksX = getCurrentXTicks();
      return [
        ticksX
          .map(tickX => {
            const scaledTick = x(tickX);
            const pathString = d3.line()([
              [scaledTick, 0],
              [scaledTick, height],
            ]);
            return pathString.replace('Z', '');
          })
          .join(),
        y
          .ticks(NUM_TICKS)
          .map(tickY => {
            const scaledTick = y(tickY);
            const pathString = d3.line()([
              [0, scaledTick],
              [width, scaledTick],
            ]);
            return pathString.replace('Z', '');
          })
          .join(),
      ].join();
    }

    let xAxis = d3
      .axisBottom(x)
      .tickValues(getCurrentXTicks())
      .tickSize(0)
      .tickFormat(hijackTickFormat);

    const yAxis = d3
      .axisLeft(y)
      .ticks(NUM_TICKS)
      .tickSize(0);

    currentContext
      .append('defs')
      .append('clipPath')
      .attr('id', 'clip')
      .append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'black');

    const focus = currentContext
      .append('g')
      .attr('class', 'focus')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const context = currentContext
      .append('g')
      .attr('class', 'context')
      .attr('transform', `translate(${margin2.left}, ${margin2.top})`);

    // Canvas scaling for different resolutions
    const cvs = canvas.node();
    // Get pixel ratio.
    const dpr = window.devicePixelRatio;
    const tmpCvsWidth = Math.ceil(width * dpr);
    const tmpCvsHeight = Math.ceil(height * dpr);

    // Set the canvas resolution dimensions (integer values).
    cvs.width = tmpCvsWidth;
    cvs.height = tmpCvsHeight;
    cvs.style.width = `${tmpCvsWidth / dpr}px`;
    cvs.style.height = `${tmpCvsHeight / dpr}px`;

    // Adjust canvas coordinates to use CSS pixel coordinates.
    const canvContext = cvs.getContext('2d');
    canvContext.scale(dpr, dpr);
    // End canvas scaling

    focus
      .append('text')
      .attr('y', height + 14)
      .attr('x', width / 2)
      .style('text-anchor', 'middle')
      .style('font-weight', 'bold')
      .text(manhattan ? 'Genome' : 'Rank');

    focus
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - margin.left)
      .attr('x', 0 - height / 2)
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('font-weight', 'bold')
      .text(selectedVariantScoreType.name);

    tooltip
      .style('visibility', 'hidden')
      .style('opacity', 1)
      .attr('class', 'tooltip')
      .style('background-color', 'rgba(255, 255, 255, 0.85)')
      .style('border', 'solid')
      .style('border-width', '1px')
      .style('border-radius', '5px')
      .style('pointer-events', 'none')
      .style('padding', '10px');

    focus
      .append('g')
      .attr('class', 'axis axis--x')
      .call(xAxis)
      .attr('transform', `translate(0, ${height - 12})`)
      .select('.domain')
      .attr('stroke-width', 0);

    focus
      .append('g')
      .attr('class', 'axis axis--y')
      .call(yAxis)
      .select('.domain')
      .attr('stroke-width', 0);

    const gridLines = focus
      .append('path')
      .attr('d', getGridLines())
      .style('stroke', 'hsl(0deg 0% 80%)')
      .style('stroke-opacity', '50%');

    const dots = focus.append('g');
    dots.attr('clip-path', 'url(#clip)');

    let timeout = null;

    const reticle = focus.append('g').style('visibility', 'hidden');
    const reticleLine = reticle.append('path').attr('stroke', 'black');
    const reticleCircle = reticle
      .append('circle')
      .attr('r', radius + 1)
      .attr('fill', 'white')
      .attr('stroke', 'black')
      .attr('cx', reticlePosition[0])
      .attr('cy', reticlePosition[1])
      .style('pointer-events', 'none');

    // Append scatter plot to main chart area
    dots
      .selectAll('circle')
      .data(sortedData)
      .enter()
      .append('circle')
      .attr('class', 'dot')
      .attr('r', radius)
      .attr('cx', d => x(d.x))
      .attr('cy', d => y(d.y))
      .style('stroke', 'black')
      .style('stroke-opacity', '20%')
      .style('fill', d => variantColor(d))
      .style('cursor', 'pointer')
      .on('mousedown', () => {
        reticleCircle.style('fill', 'yellow');
      })
      .on('mouseout', () => {
        tooltip.style('visibility', 'hidden');
        reticleCircle.style('fill', 'white');
        clearTimeout(timeout);
      })
      .on('mouseover', d => {
        reticle.style('visibility', 'visible').style('fill', 'white');

        const reticlePath = d3.path();
        reticlePath.moveTo(x(d.x), 0);
        reticlePath.lineTo(x(d.x), Math.max(y(d.y) - radius * 2, 0));
        reticlePath.moveTo(x(d.x), height);
        reticlePath.lineTo(x(d.x), Math.min(y(d.y) + radius * 2, height));

        const transDur = 150;
        const tr = d3
          .transition()
          .duration(transDur)
          .ease(d3.easeLinear);

        timeout = setTimeout(
          () => tooltip.style('visibility', 'visible'),
          transDur,
        );
        const retX = x(d.x);
        const retY = y(d.y);

        setReticlePosition([retX, retY]);
        reticleCircle
          .transition(tr)
          .attr('cx', retX)
          .attr('cy', retY);

        reticleLine.transition(tr).attr('d', reticlePath.toString());

        const ttNode = tooltip.node().getBoundingClientRect();
        const ttPad = 8;
        const ttNodeW = retX + ttNode.width + ttPad;
        const ttNodeH = retY + ttNode.height + ttPad;
        const ttLeft =
          ttNodeW + margin.left >= width
            ? retX - ttNode.width - ttPad
            : retX + ttPad;
        const ttTop =
          ttNodeH + margin.top >= height
            ? retY - ttNode.height - ttPad
            : retY + ttPad;

        tooltip
          .html(
            `${d.gene ? `<b>${d.gene}</b><br />` : ''}
            ${d.max_seq_class ? `<b>${d.max_seq_class}</b><br />` : ''}
            <b>${selectedVariantScoreType.name}:</b> ${d.y}<br />
            ${d.id ? `<b>${d.id}</b><br />` : ''}
            <b>${d.chromosome}:${d.position}</b><br />
            ${
              inputType === 'vcf'
                ? `<b>Ref allele:</b> ${d.ref_allele}<br />`
                : ''
            }
            ${
              inputType === 'vcf'
                ? `<b>Alt allele:</b> ${d.alt_allele}<br />`
                : ''
            }
            ${
              d.strand && d.strand !== '.' ? `<b>Strand:</b> ${d.strand}` : ''
            }`,
          )
          .style('left', `${ttLeft + margin.left}px`)
          .style('top', `${ttTop + margin.top}px`);
      })
      .on('click', d => dispatch(setSelectedVariantAndUpdateRange(d, genome)));

    const selectedVariantCircle = focus
      .append('g')
      .attr('clip-path', 'url(#clip)')
      .append('circle')
      .attr('r', radius + 4)
      .attr('fill', 'transparent')
      .attr('stroke', 'black')
      .style('pointer-events', 'none')
      .attr('cx', x(selectedVariantCoordinates.x))
      .attr('cy', y(selectedVariantCoordinates.y));

    // append scatter plot to brush chart area
    const brushDots = context.append('g');
    brushDots
      .selectAll('circle')
      .data(sortedData)
      .enter()
      .append('circle')
      .attr('class', 'dotContext')
      .attr('r', radius / 3)
      .attr('cx', d => x2(d.x))
      .attr('cy', d => y2(d.y))
      .style('fill', d => variantColor(d));

    brushDots // selectedVariantBrushDot
      .append('circle')
      .attr('r', radius / 1.5)
      .attr('fill', 'black')
      .attr('stroke', 'black')
      .attr('cx', x2(selectedVariantCoordinates.x))
      .attr('cy', y2(selectedVariantCoordinates.y));

    function drawPoint(point) {
      canvContext.moveTo(point[0], point[1]);
      canvContext.arc(point[0], point[1], radius, 0, 2 * Math.PI);
    }

    function draw(xDom) {
      canvContext.clearRect(0, 0, containerWidth, SVG_HEIGHT);
      Object.keys(formattedData).forEach(key => {
        formattedData[key].forEach(d => {
          canvContext.beginPath();
          canvContext.fillStyle = variantColor(d);
          drawPoint([xDom(d.x), y(d.y)]);
          canvContext.fill();
        });
      });
    }

    const brushWidth = 3;

    function setHandles() {
      context
        .selectAll('.handle')
        .attr('y', 0)
        .attr('height', height2)
        .attr('fill', 'black');

      context.selectAll('.selection').attr('height', height2);

      context
        .selectAll('.overlay')
        .style('fill-opacity', 0.5)
        .attr('height', height2)
        .attr('fill', () => (manhattan ? 'url(#brushGradient)' : 'lightGrey'));

      context
        .selectAll('.handle--w')
        .attr('transform', () => `translate(${brushWidth / 2}, 0)`);

      context
        .selectAll('.handle--e')
        .attr('transform', () => `translate(-${brushWidth / 2}, 0)`);
    }

    // Create brush function redraw scatterplot with selection
    function brushed() {
      const { selection } = d3.event;
      if (
        !d3.event.sourceEvent ||
        !selection ||
        selection[0] === selection[1] ||
        d3.event.sourceEvent.type !== 'mousemove'
      )
        return;
      dots.style('visibility', 'hidden');
      reticle.style('visibility', 'hidden');
      tooltip.style('visibility', 'hidden');

      const tmpXDomain = selection.map(x2.invert, x2);
      x.domain(tmpXDomain);
      setXDomain(tmpXDomain);
      selectedVariantCircle
        .attr('cx', x(selectedVariantCoordinates.x))
        .attr('cy', y(selectedVariantCoordinates.y));
      draw(x);
      xAxis = d3
        .axisBottom(x)
        .tickValues(getCurrentXTicks())
        .tickSize(0)
        .tickFormat(hijackTickFormat);
      focus.select('.axis--x').call(xAxis);
      gridLines.attr('d', getGridLines());
      setHandles();
    }

    function brushEnd() {
      const { selection } = d3.event;
      if (
        !d3.event.sourceEvent ||
        !selection ||
        selection[0] === selection[1] ||
        d3.event.sourceEvent.type !== 'mouseup'
      )
        return;
      const tmpXDomain = selection.map(x2.invert, x2);
      x.domain(tmpXDomain);
      setXDomain(tmpXDomain);
      dots.selectAll('circle').attr('cx', (d, i) => x(sortedData[i].x));
      dots.style('visibility', 'visible');

      selectedVariantCircle
        .attr('cx', x(selectedVariantCoordinates.x))
        .attr('cy', y(selectedVariantCoordinates.y));

      canvContext.clearRect(0, 0, containerWidth, SVG_HEIGHT);
    }

    function brushStart() {
      const { selection } = d3.event;
      if (
        !d3.event.sourceEvent ||
        !selection ||
        selection[0] === selection[1] ||
        d3.event.sourceEvent.type !== 'mousedown'
      )
        return;
      const tmpXDomain = selection.map(x2.invert, x2);
      x.domain(tmpXDomain);
      setXDomain(tmpXDomain);
      draw(x);
      dots.style('visibility', 'hidden');
      reticle.style('visibility', 'hidden');
      tooltip.style('visibility', 'hidden');

      selectedVariantCircle
        .attr('cx', x(selectedVariantCoordinates.x))
        .attr('cy', y(selectedVariantCoordinates.y));

      setHandles();
    }

    const brush = d3
      .brushX()
      .extent([
        [0, 0],
        [width, height2],
      ])
      .on('start', brushStart)
      .on('brush end', brushed)
      .on('end', brushEnd)
      .handleSize(brushWidth);

    const gBrush = context
      .append('g')
      .attr('class', 'brush')
      .call(brush)
      .call(
        brush.move,
        xDomain ? [x2(xDomain[0]), x2(xDomain[1])] : [0, width],
      );

    svg.on('wheel.zoom', function onWheelZoom() {
      reticle.style('visibility', 'hidden');
      tooltip.style('visibility', 'hidden');
      const direction = d3.event.wheelDelta < 0 ? -1 : 1;
      const currentDomain = d3.brushSelection(gBrush.node()) || [0, 0];
      const brushFocalPoint = d3.mouse(this)[0];
      const selectionWidth =
        width - (width - currentDomain[1]) - currentDomain[0];
      const eRatio = (width - brushFocalPoint) / selectionWidth;
      const wRatio = brushFocalPoint / width;

      let newW = null;
      let newE = null;

      function _getNewDomain(zoomIncrement) {
        const w = ((selectionWidth * wRatio) / zoomIncrement) * direction;
        const e = ((selectionWidth * eRatio) / zoomIncrement) * direction;
        newW = currentDomain[0] + w < 0 ? 0 : currentDomain[0] + w;
        newE = currentDomain[1] - e > width ? width : currentDomain[1] - e;
        if (newE < newW) _getNewDomain(zoomIncrement * 2);
      }

      _getNewDomain(20);
      if (newW === newE) return;
      const newSelection = [newW, newE];
      brush.move(gBrush, newSelection);
      const tmpXDomain = newSelection.map(x2.invert, x2);
      x.domain(tmpXDomain);
      setXDomain(tmpXDomain);

      selectedVariantCircle
        .attr('cx', x(selectedVariantCoordinates.x))
        .attr('cy', y(selectedVariantCoordinates.y));

      dots.selectAll('circle').attr('cx', (d, i) => x(sortedData[i].x));
    });

    setHandles();

    // Hide reticle if user alt-tabs out
    window.onblur = () => {
      reticle.style('visibility', 'hidden');
      tooltip.style('visibility', 'hidden');
    };

    return () => currentContext.remove();
  }, [
    canvasRef,
    containerWidth,
    formattedData,
    maxY,
    minY,
    selectedVariant,
    sortedData,
    svgRef,
    seqClass,
  ]);

  return (
    <div
      ref={divRef}
      className={s.scatterContainer}
      style={{ position: 'relative' }}
    >
      <canvas
        style={{
          border: '1px solid hsl(0deg 0% 80%)',
          position: 'absolute',
          left: margin.left,
          top: margin.top,
          zIndex: -1,
        }}
        ref={canvasRef}
        width={containerWidth - margin.left - margin.right}
        height={SVG_HEIGHT - margin.top - margin.bottom}
      />
      <svg ref={svgRef} width={containerWidth} height={SVG_HEIGHT} />
      <div ref={tooltipRef} />
    </div>
  );
};

VariantScatterContainer.propTypes = {
  dataRegions: PropTypes.shape({}).isRequired,
  manhattan: PropTypes.bool.isRequired,
  seqClass: PropTypes.bool.isRequired,
  selectedVariantScoreType: PropTypes.shape({
    name: PropTypes.string,
    level: PropTypes.string,
    short_name: PropTypes.string,
  }).isRequired,
  sortedData: PropTypes.arrayOf(PropTypes.object).isRequired,
  tickFormat: PropTypes.func.isRequired,
  inputType: PropTypes.string,
};

export default withStyles(s)(VariantScatterContainer);
